import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import LoaderBar from '../../components/loader-bar/LoaderBar';
import Image from '../../components/image/Image';
import { formatMoneyKr, pluralize } from '../../utils';
import { fetchSchoolSummaryByEvent } from '../../project/project-actions';
import { getEvent } from '../../event/event-reducer';
import { fetchEvent } from '../../event/event-actions';
import { useProject } from '../../project/project-hooks/useProject';
import TrackEventLink from '../../components/track-event-link/TrackEventLink';
import { fetchCauseForProject } from '../../template/template-actions';
import { getCauseForProjectId } from '../../template/template-reducer';
import { Heading5 } from '@sb1/ffe-core-react';
import styles from './school-event-project-banner.module.less';
import { TEMPLATE_CAUSE_TYPE } from '../../template/template-model';

const SchoolEventProjectBanner = () => {
    const project = useProject();
    const eventId = project.event_id;
    const projectId = project.id;
    const dispatch = useDispatch();
    const [school, setSchool] = useState(null);
    const [projectCount, setProjectCount] = useState(0);
    const [amountCollected, setAmountCollected] = useState(0);
    const [error, setError] = useState(null);
    const event = useSelector((state) => getEvent(state, eventId));
    const [isLoading, setIsLoading] = useState(true);
    const templateCause = useSelector((state) => getCauseForProjectId(state, project?.id));

    const setStateFromResponse = (res) => {
        setProjectCount(res.schoolSummary.project_count);
        setAmountCollected(res.schoolSummary.collected_amount);
        setSchool(res.school);
    };

    useEffect(() => {
        if (project?.template_id) {
            dispatch(fetchCauseForProject({ projectId: project.id }));
        }
    }, [dispatch, project.id, project?.template_id]);

    useEffect(() => {
        if (projectId) {
            dispatch(fetchSchoolSummaryByEvent({ projectId }))
                .then((res) => setStateFromResponse(res))
                .catch((err) => {
                    if (err?.status !== 404) {
                        setError(true);
                    }
                })
                .finally(() => setIsLoading(false));
        }
        if (eventId) {
            dispatch(fetchEvent(eventId));
        }
    }, [projectId, eventId]);

    if (isLoading) {
        return <LoaderBar />;
    }

    if (!school || !event) {
        return null;
    }

    if (templateCause?.type === TEMPLATE_CAUSE_TYPE.SCHOOL_VGS) {
        return (
            <div className={styles.causeEventProjectBannerWrapper}>
                <Image className={styles.causeEventImage} src={`bok_no_background.svg`} alt={''} />
                <div className={styles.causeEventInfoWrapper}>
                    {!error && (
                        <>
                            <Heading5 noMargin>{school?.name}</Heading5>
                            <div>
                                <strong>{formatMoneyKr(amountCollected)}</strong> fordelt på{' '}
                                <strong>
                                    {projectCount} {pluralize(parseInt(projectCount, 10), 'spleis', 'spleiser')}
                                </strong>
                            </div>
                        </>
                    )}
                    <TrackEventLink
                        category={'school-event-banner'}
                        action={'link-click'}
                        properties={{
                            cause_id: templateCause.id,
                            school_id: school?.id,
                            event_id: event?.id,
                            project_id: projectId,
                        }}
                        to={`/cause/${templateCause.id}`}
                    >
                        Se alle spleisene for innsamlingsaksjonen
                    </TrackEventLink>
                </div>
            </div>
        );
    }

    const schoolBannerInfo = event.profile?.schoolBannerInfo || {};
    const schoolPageLink = `/school-event/${schoolBannerInfo.eventName}/${school.organizationNumber}`;

    return (
        <div className={styles.bannerContainer}>
            <div className={styles.eventContainer}>
                {event?.img_url && <Image className={styles.image} src={event.img_url} alt={''} />}
                <div>
                    <p className={styles.titleText}>{event.name}</p>
                    <TrackEventLink
                        category={'school-event-banner'}
                        action={'event-link-click'}
                        properties={{ eventName: schoolBannerInfo.eventName }}
                        to={schoolBannerInfo.linkTo}
                    >
                        <span className={styles.linkText}>{schoolBannerInfo.linkText || 'Se alle spleisene'}</span>
                    </TrackEventLink>
                </div>
            </div>
            {!error && (
                <>
                    <div>
                        <p className={styles.titleText}>
                            {schoolBannerInfo?.eventName ? (
                                <TrackEventLink
                                    category={'school-event-banner'}
                                    action={'school-link-click'}
                                    properties={{ eventName: schoolBannerInfo.eventName }}
                                    to={schoolPageLink}
                                >
                                    {school.name}
                                </TrackEventLink>
                            ) : (
                                <span>{school.name}</span>
                            )}
                        </p>
                        <div>
                            <span className={styles.currencyText}>{formatMoneyKr(amountCollected)}</span>{' '}
                            <span>innsamlet ({projectCount} spleiser)</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SchoolEventProjectBanner;
